import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  UserPlus, 
  Shield, 
  Users as UsersIcon,
  Search,
  Mail,
  Phone,
  Store,
  Ban,
  CheckCircle,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UsersPage = () => {
  const { isAdmin, isSuperUser } = useAuth();
  const { stores } = useStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    user_type: 'staff',
    assigned_stores: [],
    creditor_assignments: []
  });
  const [saving, setSaving] = useState(false);
  const [availableCreditors, setAvailableCreditors] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditorsForStores = async (storeIds) => {
    if (!storeIds || storeIds.length === 0) {
      setAvailableCreditors([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/users/creditors-for-stores?store_ids=${storeIds.join(',')}`);
      setAvailableCreditors(response.data);
    } catch (error) {
      console.error('Failed to load creditors:', error);
      setAvailableCreditors([]);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        phone: user.phone || '',
        password: '',
        user_type: user.user_type,
        assigned_stores: user.assigned_stores || [],
        creditor_assignments: user.creditor_assignments || []
      });
      // If editing a creditor user, fetch available creditors
      if (user.user_type === 'creditor' && user.assigned_stores?.length > 0) {
        fetchCreditorsForStores(user.assigned_stores);
      }
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        password: 'TempPass123!',
        user_type: 'staff',
        assigned_stores: [],
        creditor_assignments: []
      });
      setAvailableCreditors([]);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, formData);
        toast.success('User updated successfully');
      } else {
        const response = await axios.post(`${API}/users`, formData);
        if (response.data.email_status?.success) {
          toast.success(`User created! Password setup email sent to ${formData.email}`);
        } else {
          toast.success(`User created! Email delivery may have failed - please use password reset.`);
        }
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/suspend`);
      toast.success('User suspended');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to suspend user');
    }
  };

  const handleActivate = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/activate`);
      toast.success('User activated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to activate user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleStoreToggle = (storeId) => {
    setFormData(prev => ({
      ...prev,
      assigned_stores: prev.assigned_stores.includes(storeId)
        ? prev.assigned_stores.filter(id => id !== storeId)
        : [...prev.assigned_stores, storeId]
    }));
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.user_type === filterRole;
    return matchesSearch && matchesRole;
  });

  const getStatusBadge = (status) => {
    const styles = {
      active: 'status-active',
      suspended: 'status-suspended',
      pending_password_change: 'status-pending'
    };
    return styles[status] || 'bg-gray-500/20 text-gray-400';
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_user: 'bg-purple-500/20 text-purple-400',
      admin: 'bg-blue-500/20 text-blue-400',
      manager: 'bg-green-500/20 text-green-400',
      staff: 'bg-gray-500/20 text-gray-400',
      creditor: 'bg-amber-500/20 text-amber-400'
    };
    return styles[role] || 'bg-gray-500/20 text-gray-400';
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            User Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage users, roles, and store assignments
          </p>
        </div>

        <Button onClick={() => handleOpenDialog()} data-testid="add-user-btn">
          <UserPlus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-users"
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-role">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {isSuperUser && <SelectItem value="super_user">Super User</SelectItem>}
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="creditor">Creditor</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <UsersIcon className="h-12 w-12 mb-4 opacity-50" />
              <p>No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left p-4 font-medium text-muted-foreground">User</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Role</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Stores</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b border-border/50 table-row-hover" data-testid={`user-row-${user.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="text-primary font-semibold">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {user.email}
                            </p>
                            {user.phone && (
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {user.phone}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge className={getRoleBadge(user.user_type)}>
                          {(user.user_type || 'staff').replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge className={getStatusBadge(user.status)}>
                          {(user.status || 'active').replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {user.assigned_stores?.length > 0 ? (
                            user.assigned_stores.slice(0, 2).map(storeId => {
                              const store = stores.find(s => s.id === storeId);
                              return (
                                <Badge key={storeId} variant="outline" className="text-xs">
                                  {store?.name || 'Unknown'}
                                </Badge>
                              );
                            })
                          ) : (
                            <span className="text-sm text-muted-foreground">All stores</span>
                          )}
                          {user.assigned_stores?.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{user.assigned_stores.length - 2} more
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {(user.status === 'active' || user.status === 'pending_password_change') ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleSuspend(user.id)}
                              className="text-yellow-500 hover:text-yellow-400"
                              title="Suspend user"
                              data-testid={`suspend-user-${user.id}`}
                            >
                              <Ban className="h-4 w-4" />
                            </Button>
                          ) : user.status === 'suspended' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleActivate(user.id)}
                              className="text-green-500 hover:text-green-400"
                              title="Activate user"
                              data-testid={`activate-user-${user.id}`}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(user.id)}
                            className="text-destructive hover:text-destructive/80"
                            title="Delete user"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingUser ? 'Edit User' : 'Add New User'}
            </DialogTitle>
            <DialogDescription>
              {editingUser ? 'Update user details and permissions' : 'Create a new user account'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="user-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={!!editingUser}
                data-testid="user-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone (Optional)</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="user-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="user_type">Role</Label>
              <Select 
                value={formData.user_type} 
                onValueChange={(value) => setFormData({ ...formData, user_type: value })}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isSuperUser && <SelectItem value="super_user">Super User</SelectItem>}
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="creditor">Creditor</SelectItem>
                </SelectContent>
              </Select>
              {formData.user_type === 'creditor' && (
                <p className="text-xs text-amber-400 mt-1">
                  Creditor users can only access credit/repayment request functionality.
                </p>
              )}
            </div>

            {stores.length > 0 && (
              <div className="space-y-2">
                <Label>Assigned Stores</Label>
                <p className="text-xs text-muted-foreground">
                  {isSuperUser 
                    ? 'Select stores to assign to this user' 
                    : 'You can only assign stores that are allocated to you'}
                </p>
                <div className="border border-border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                  {stores.map(store => (
                    <div key={store.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`store-${store.id}`}
                        checked={formData.assigned_stores.includes(store.id)}
                        onCheckedChange={() => {
                          handleStoreToggle(store.id);
                          // For creditor users, fetch available creditors when stores change
                          if (formData.user_type === 'creditor') {
                            const newStores = formData.assigned_stores.includes(store.id)
                              ? formData.assigned_stores.filter(s => s !== store.id)
                              : [...formData.assigned_stores, store.id];
                            fetchCreditorsForStores(newStores);
                          }
                        }}
                      />
                      <label
                        htmlFor={`store-${store.id}`}
                        className="text-sm cursor-pointer flex-1"
                      >
                        {store.name}
                        {formData.user_type === 'creditor' && !store.creditor_request_enabled && (
                          <span className="text-xs text-red-400 ml-2">(Creditor requests disabled)</span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Creditor Assignments - Only show for creditor user type */}
            {formData.user_type === 'creditor' && formData.assigned_stores.length > 0 && (
              <div className="space-y-2">
                <Label>Creditor Accounts</Label>
                <p className="text-xs text-muted-foreground">
                  Select which creditor accounts this user can access
                </p>
                {availableCreditors.length === 0 ? (
                  <p className="text-xs text-amber-400 p-2 border rounded">
                    No creditors found for selected stores. Create creditors in Finance → Creditors first.
                  </p>
                ) : (
                  <div className="border border-border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                    {availableCreditors.map(creditor => {
                      const storeName = stores.find(s => s.id === creditor.store_id)?.name || 'Unknown Store';
                      const isAssigned = formData.creditor_assignments.some(
                        ca => ca.store_id === creditor.store_id && ca.creditor_id === creditor.id
                      );
                      return (
                        <div key={`${creditor.store_id}-${creditor.id}`} className="flex items-center space-x-2">
                          <Checkbox
                            id={`creditor-${creditor.id}`}
                            checked={isAssigned}
                            onCheckedChange={() => {
                              if (isAssigned) {
                                setFormData({
                                  ...formData,
                                  creditor_assignments: formData.creditor_assignments.filter(
                                    ca => !(ca.store_id === creditor.store_id && ca.creditor_id === creditor.id)
                                  )
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  creditor_assignments: [
                                    ...formData.creditor_assignments,
                                    { store_id: creditor.store_id, creditor_id: creditor.id }
                                  ]
                                });
                              }
                            }}
                          />
                          <label
                            htmlFor={`creditor-${creditor.id}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {creditor.name}
                            <span className="text-xs text-muted-foreground ml-2">({storeName})</span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-user-btn">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  editingUser ? 'Update User' : 'Create User'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
